/**
 * CrossBranchPaymentManager — non-blocking global floating pill notification system
 * for cross-branch credit payments.
 *
 * Design spec: /app/memory/CROSS_BRANCH_PAYMENT_DESIGN.md — §2 (User Flow)
 *
 * Architecture:
 * - Amber pill: collecting branch (Branch A) — outgoing payments
 *   → shows count of active outgoing payments
 *   → shows OTP code when status=otp_pending
 * - Teal pill: credit branch (Branch B) — payments needing review
 *   → shows count of pending reviews
 *   → opens review dialog to confirm/adjust
 *
 * Polling: 15 seconds (or 5s after state change)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Receipt, X, ChevronDown, ChevronUp, Check, Eye, RefreshCw, Loader2 } from 'lucide-react';

const POLL_INTERVAL_MS = 15_000;
const QUICK_POLL_MS   = 5_000;

function statusColor(status) {
  return {
    pending_review: 'bg-yellow-100 text-yellow-800',
    confirmed:      'bg-blue-100 text-blue-800',
    otp_pending:    'bg-emerald-100 text-emerald-800',
    finalized:      'bg-green-100 text-green-800',
    cancelled:      'bg-red-100 text-red-800',
    expired:        'bg-gray-100 text-gray-700',
  }[status] || 'bg-gray-100 text-gray-600';
}

function statusLabel(status) {
  return {
    pending_review: 'Pending Review',
    confirmed:      'Confirmed',
    otp_pending:    'OTP Ready',
    finalized:      'Done',
    cancelled:      'Cancelled',
    expired:        'Expired',
  }[status] || status;
}

const php = (n) => '₱' + (parseFloat(n) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Credit Branch Review Dialog ────────────────────────────────────────────
function ReviewDialog({ payment, onClose, onRefresh }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [generatedOtp, setGeneratedOtp] = useState(null); // show after confirm

  useEffect(() => {
    loadDetail();
  }, [payment.id]); // eslint-disable-line

  const loadDetail = async () => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/cross-branch-payments/${payment.id}`);
      const detail = res.data;
      setInvoices((detail.invoices || []).map(inv => ({
        ...inv,
        amount_applied: inv.amount_applied,
        interest_applied: inv.interest_applied || 0,
      })));
    } catch {
      toast.error('Failed to load payment details');
    }
    setLoadingDetail(false);
  };

  const updateInvoice = (idx, field, val) => {
    setInvoices(prev => prev.map((inv, i) => i === idx ? { ...inv, [field]: val } : inv));
  };

  const totalAmount = invoices.reduce((s, inv) => s + (parseFloat(inv.amount_applied) || 0), 0);
  const totalInterest = invoices.reduce((s, inv) => s + (parseFloat(inv.interest_applied) || 0), 0);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/cross-branch-payments/${payment.id}/confirm`, {
        invoices: invoices.map(inv => ({
          invoice_id: inv.invoice_id,
          amount_applied: parseFloat(inv.amount_applied) || 0,
          interest_applied: parseFloat(inv.interest_applied) || 0,
        })),
      });
      // Show the OTP to Branch B so they can call Branch A
      const otp = res.data?.otp_code;
      if (otp) {
        setGeneratedOtp(otp);
      } else {
        toast.success('Payment confirmed — OTP generated');
        onRefresh();
        onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to confirm');
    }
    setLoading(false);
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this cross-branch payment?')) return;
    setLoading(true);
    try {
      await api.post(`/cross-branch-payments/${payment.id}/cancel`, { reason: 'Declined by credit branch' });
      toast.success('Payment cancelled');
      onRefresh();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="cbp-review-dialog">
      {/* Show OTP Display when generated */}
      {generatedOtp && (
        <OTPDisplayDialog
          otp={generatedOtp}
          paymentNumber={payment.payment_number}
          collectingBranchName={payment.collecting_branch_name}
          onClose={() => { setGeneratedOtp(null); onRefresh(); onClose(); }}
          onRegenerate={async () => {
            // D9 (BT Batch 2 P5): a lost/expired code no longer kills the
            // payment — mint a fresh one in place.
            try {
              const res = await api.post(`/cross-branch-payments/${payment.id}/generate-otp`);
              if (res.data?.otp_code) { setGeneratedOtp(res.data.otp_code); toast.success('New code generated'); }
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Could not generate a new code');
            }
          }}
        />
      )}
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 bg-teal-50 border-b border-teal-100">
          <div>
            <h2 className="text-base font-semibold text-teal-900" data-testid="review-dialog-title">
              Review Payment — {payment.payment_number}
            </h2>
            <p className="text-xs text-teal-600 mt-0.5">
              From: <strong>{payment.collecting_branch_name}</strong> · Customer: {payment.customer_name}
            </p>
          </div>
          <button onClick={onClose} className="text-teal-400 hover:text-teal-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {loadingDetail ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <>
              <div className="space-y-3">
                {invoices.map((inv, i) => (
                  <div key={inv.invoice_id} className="p-3 rounded-lg border border-slate-100" data-testid={`review-invoice-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{inv.invoice_number}</span>
                      <span className="text-xs text-slate-400">Balance: {php(inv.original_balance)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-slate-500 uppercase tracking-wider">Amount</label>
                        <input
                          type="number"
                          className="w-full border rounded px-2 py-1 text-sm text-right"
                          value={inv.amount_applied}
                          onChange={e => updateInvoice(i, 'amount_applied', e.target.value)}
                          data-testid={`review-amount-${i}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 uppercase tracking-wider">Interest</label>
                        <input
                          type="number"
                          className="w-full border rounded px-2 py-1 text-sm text-right"
                          value={inv.interest_applied}
                          onChange={e => updateInvoice(i, 'interest_applied', e.target.value)}
                          data-testid={`review-interest-${i}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-lg bg-slate-50 flex items-center justify-between">
                <span className="text-sm text-slate-600">Grand Total:</span>
                <span className="text-lg font-bold" data-testid="review-grand-total">
                  {php(totalAmount + totalInterest)}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2 p-4 border-t border-slate-100 bg-slate-50">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="flex-1 px-4 py-2 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
            data-testid="review-cancel-btn"
          >
            Decline
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || loadingDetail}
            className="flex-1 px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-1"
            data-testid="review-confirm-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Confirm & Generate OTP
          </button>
        </div>
      </div>
    </div>
  );
}

// ── OTP Display Dialog (Branch B sees the OTP after confirming) ────────────
function OTPDisplayDialog({ otp, paymentNumber, collectingBranchName, onClose, onRegenerate }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(otp).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="cbp-otp-display-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-5 py-4 bg-teal-600 text-white text-center">
          <div className="text-xs font-semibold tracking-wider uppercase opacity-75 mb-1">OTP Generated — {paymentNumber}</div>
          <div className="text-[11px] opacity-75">Call <strong>{collectingBranchName}</strong> and give them this code</div>
        </div>
        <div className="p-6 text-center space-y-4">
          <p className="text-xs text-slate-500">Read this code to the receiving branch so they can finalize the payment:</p>
          <div className="bg-teal-50 border-2 border-teal-300 rounded-xl py-5 px-4">
            <div className="text-5xl font-mono font-bold tracking-[0.35em] text-teal-700 select-all" data-testid="otp-display-code">
              {otp}
            </div>
          </div>
          <button onClick={copy}
            className="text-xs text-teal-600 hover:text-teal-800 underline"
            data-testid="otp-copy-btn">
            {copied ? '✓ Copied' : 'Copy code'}
          </button>
          <p className="text-[10px] text-slate-400">Code expires in 15 minutes · Max 5 entry attempts</p>
        </div>
        <div className="flex gap-2 px-5 pb-5">
          {onRegenerate && (
            <button onClick={onRegenerate}
              className="flex-1 text-xs py-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
              data-testid="otp-regenerate-btn">
              Regenerate
            </button>
          )}
          <button onClick={onClose}
            className="flex-1 text-xs py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700"
            data-testid="otp-display-done-btn">
            Done — I've called them
          </button>
        </div>
      </div>
    </div>
  );
}


function OTPEntryDialog({ payment, onClose, onRefresh }) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!otp) return;
    setLoading(true);
    try {
      await api.post(`/cross-branch-payments/${payment.id}/verify-otp`, { otp_code: otp });
      toast.success('Payment finalized — AR updated');
      onRefresh();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'OTP verification failed');
      setOtp('');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="cbp-otp-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-5 py-4 bg-emerald-50 border-b border-emerald-100">
          <h2 className="text-base font-semibold text-emerald-900">Enter OTP — {payment.payment_number}</h2>
          <p className="text-xs text-emerald-600 mt-0.5">
            Call {payment.credit_branch_name} for the OTP code
          </p>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-center">
            <p className="text-sm text-slate-600 mb-1">Customer: <strong>{payment.customer_name}</strong></p>
            <p className="text-lg font-bold">{php(payment.grand_total)}</p>
          </div>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className="w-full text-center text-3xl font-mono tracking-[0.3em] border-2 border-emerald-200 rounded-lg px-4 py-3 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
            placeholder="_ _ _ _ _ _"
            maxLength={6}
            value={otp}
            onChange={e => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={e => e.key === 'Enter' && otp.length === 6 && handleVerify()}
            autoFocus
            data-testid="otp-input"
          />
          <button
            onClick={handleVerify}
            disabled={loading || otp.length < 6}
            className="w-full px-4 py-2.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1"
            data-testid="verify-otp-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Verify & Finalize
          </button>
        </div>
        <div className="px-5 pb-4">
          <button onClick={onClose} className="w-full text-xs text-slate-400 hover:text-slate-600">Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Manager Component ────────────────────────────────────────────────
export default function CrossBranchPaymentManager() {
  const { user } = useAuth();
  const [collectingPayments, setCollectingPayments] = useState([]);
  const [creditPayments, setCreditPayments] = useState([]);
  const [expandCollecting, setExpandCollecting] = useState(false);
  const [expandCredit, setExpandCredit] = useState(false);
  const [reviewPayment, setReviewPayment] = useState(null);
  const [otpPayment, setOtpPayment] = useState(null);
  const [showOtpDisplay, setShowOtpDisplay] = useState(null); // {otp, payment_number, collecting_branch_name}
  const pollRef = useRef(null);
  const quickPollRef = useRef(null);

  const hasCBPPermission = useCallback(() => {
    if (!user) return false;
    const perms = user.permissions || {};
    return perms?.cross_branch_payments?.view || user.role === 'admin' || user.role === 'owner';
  }, [user]);

  const poll = useCallback(async () => {
    if (!hasCBPPermission()) return;
    try {
      const [collectingRes, creditRes] = await Promise.all([
        api.get('/cross-branch-payments/collecting/status'),
        api.get('/cross-branch-payments/credit/pending'),
      ]);
      setCollectingPayments(collectingRes.data.payments || []);
      setCreditPayments(creditRes.data.payments || []);
    } catch { /* silent */ }
  }, [hasCBPPermission]);

  const quickRefresh = useCallback(() => {
    poll();
    clearTimeout(quickPollRef.current);
    quickPollRef.current = setTimeout(poll, QUICK_POLL_MS);
  }, [poll]);

  useEffect(() => {
    if (!hasCBPPermission()) return;
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => { clearInterval(pollRef.current); clearTimeout(quickPollRef.current); };
  }, [poll, hasCBPPermission]);

  if (!hasCBPPermission()) return null;

  const collectingCount = collectingPayments.length;
  const creditCount = creditPayments.length;

  if (collectingCount === 0 && creditCount === 0) return null;

  return (
    <>
      {/* Collecting Branch Pill (amber) */}
      {collectingCount > 0 && (
        <div className="fixed bottom-20 right-4 z-40" data-testid="cbp-collecting-pill">
          <button
            onClick={() => setExpandCollecting(!expandCollecting)}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500 text-white shadow-lg hover:bg-amber-600 transition-all animate-pulse"
            data-testid="cbp-collecting-pill-btn"
          >
            <Receipt className="w-4 h-4" />
            <span className="text-sm font-medium">{collectingCount} payment{collectingCount > 1 ? 's' : ''} pending</span>
            {expandCollecting ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
          </button>

          {expandCollecting && (
            <div className="absolute bottom-12 right-0 w-80 bg-white rounded-xl shadow-2xl border border-amber-100 overflow-hidden" data-testid="cbp-collecting-dropdown">
              <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
                <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Outgoing Payments</span>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {collectingPayments.map(p => (
                  <div key={p.id} className="px-4 py-3 border-b border-slate-50 hover:bg-slate-50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{p.payment_number}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColor(p.status)}`}>
                        {statusLabel(p.status)}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {p.customer_name} → {p.credit_branch_name} · {php(p.grand_total)}
                    </div>
                    {p.status === 'otp_pending' && (
                      <div className="mt-2">
                        <button
                          onClick={() => { setOtpPayment(p); setExpandCollecting(false); }}
                          className="w-full text-xs px-3 py-1.5 rounded bg-emerald-500 text-white hover:bg-emerald-600 flex items-center justify-center gap-1"
                          data-testid={`enter-otp-${p.id}`}
                        >
                          Enter OTP
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Credit Branch Pill (teal) */}
      {creditCount > 0 && (
        <div className="fixed bottom-4 right-4 z-40" data-testid="cbp-credit-pill">
          <button
            onClick={() => setExpandCredit(!expandCredit)}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500 text-white shadow-lg hover:bg-teal-600 transition-all animate-pulse"
            data-testid="cbp-credit-pill-btn"
          >
            <Eye className="w-4 h-4" />
            <span className="text-sm font-medium">{creditCount} need{creditCount > 1 ? '' : 's'} review</span>
            {expandCredit ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
          </button>

          {expandCredit && (
            <div className="absolute bottom-12 right-0 w-80 bg-white rounded-xl shadow-2xl border border-teal-100 overflow-hidden" data-testid="cbp-credit-dropdown">
              <div className="px-4 py-3 bg-teal-50 border-b border-teal-100">
                <span className="text-xs font-semibold text-teal-800 uppercase tracking-wider">Needs Your Review</span>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {creditPayments.map(p => (
                  <div key={p.id} className="px-4 py-3 border-b border-slate-50 hover:bg-slate-50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{p.payment_number}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColor(p.status)}`}>
                        {statusLabel(p.status)}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {p.customer_name} from {p.collecting_branch_name} · {php(p.grand_total)}
                    </div>
                    {p.status === 'pending_review' && (
                      <button
                        onClick={() => { setReviewPayment(p); setExpandCredit(false); }}
                        className="mt-2 w-full text-xs px-3 py-1.5 rounded bg-teal-500 text-white hover:bg-teal-600 flex items-center justify-center gap-1"
                        data-testid={`review-btn-${p.id}`}
                      >
                        <Eye className="w-3 h-3" /> Review
                      </button>
                    )}
                    {p.status === 'otp_pending' && p.otp_code && (
                      <div className="mt-2 space-y-1.5">
                        <div className="bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-center">
                          <div className="text-[10px] text-teal-600 mb-0.5">OTP — Call {p.collecting_branch_name}:</div>
                          <div className="text-xl font-mono font-bold tracking-[0.25em] text-teal-700" data-testid={`credit-otp-${p.id}`}>{p.otp_code}</div>
                        </div>
                        <button
                          onClick={() => setShowOtpDisplay({ otp: p.otp_code, payment_number: p.payment_number, collecting_branch_name: p.collecting_branch_name, id: p.id })}
                          className="w-full text-[10px] px-2 py-1 rounded border border-teal-200 text-teal-600 hover:bg-teal-50"
                          data-testid={`show-otp-${p.id}`}>
                          Show full screen
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const res = await api.post(`/cross-branch-payments/${p.id}/generate-otp`);
                              if (res.data?.otp_code) { toast.success('New code generated'); onRefresh(); }
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Could not generate a new code');
                            }
                          }}
                          className="w-full text-[10px] px-2 py-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                          data-testid={`resend-otp-${p.id}`}>
                          Generate new code
                        </button>
                      </div>
                    )}
                    {p.status === 'otp_pending' && !p.otp_code && (
                      <button
                        onClick={async () => {
                          try {
                            const res = await api.post(`/cross-branch-payments/${p.id}/generate-otp`);
                            if (res.data?.otp_code) { toast.success('New code generated'); onRefresh(); }
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || 'Could not generate a new code');
                          }
                        }}
                        className="mt-2 w-full text-[10px] px-2 py-1 rounded border border-teal-200 text-teal-600 hover:bg-teal-50"
                        data-testid={`resend-otp-${p.id}`}>
                        Generate new code
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Review Dialog */}
      {reviewPayment && (
        <ReviewDialog
          payment={reviewPayment}
          onClose={() => setReviewPayment(null)}
          onRefresh={quickRefresh}
        />
      )}

      {/* OTP Entry Dialog */}
      {otpPayment && (
        <OTPEntryDialog
          payment={otpPayment}
          onClose={() => setOtpPayment(null)}
          onRefresh={quickRefresh}
        />
      )}

      {/* OTP Display Dialog (Branch B shows OTP to relay to Branch A) */}
      {showOtpDisplay && (
        <OTPDisplayDialog
          otp={showOtpDisplay.otp}
          paymentNumber={showOtpDisplay.payment_number}
          collectingBranchName={showOtpDisplay.collecting_branch_name}
          onClose={() => setShowOtpDisplay(null)}
        />
      )}
    </>
  );
}
